import React, { useState } from 'react';
import { UserProfile } from '../../types/profile';
import { useUserState } from '../../hooks/useUserState';
import { AuthModal } from '../auth/AuthModal';
import audioFileService from '../../services/audioFileService';

interface HeaderProps {
  profile?: UserProfile | null;
  onProfileClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ profile, onProfileClick }) => {
  const { userState } = useUserState();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // Optionally refresh the page to load user data
    window.location.reload();
  };

  const handleProfileClick = () => {
    audioFileService.play('click');
    if (onProfileClick) {
      onProfileClick();
    }
  };

  const handleSignInClick = () => {
    audioFileService.play('click');
    if (!userState.canSignIn) {
      alert('Sign in requires an internet connection. You can continue playing offline!');
      return;
    }
    setShowAuthModal(true);
  };

  return (
    <>
      <header className="bg-white shadow-lg border-b-4 border-banana-400">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="text-4xl">🍌</div>
              <div>
                <h1 className="text-2xl md:text-3xl font-display font-bold text-banana-800">
                  How Many Bananas?
                </h1>
                <p className="text-sm text-banana-600 hidden md:block">
                  The ultimate size comparison game!
                </p>
              </div>
            </div>

            {/* Right Side - Profile */}
            <div className="flex items-center space-x-4">
              {userState.isAuthenticated ? (
                <>
                  {/* Authenticated User Profile */}
                  {profile && onProfileClick && (
                    <button
                      onClick={handleProfileClick}
                      className="flex items-center space-x-3 bg-orange-100 hover:bg-orange-200 rounded-lg px-4 py-2 transition-colors"
                    >
                      <span className="text-2xl">{profile.avatar}</span>
                      <div className="text-left hidden sm:block">
                        <div className="font-medium text-gray-800">{profile.username}</div>
                        <div className="text-sm text-gray-600">
                          {profile.stats.totalGames} games • {profile.stats.bestScore} best
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Guest User Profile */}
                  {profile && onProfileClick && (
                    <button
                      onClick={handleProfileClick}
                      className="flex items-center space-x-3 bg-gray-100 hover:bg-gray-200 rounded-lg px-4 py-2 transition-colors"
                    >
                      <span className="text-2xl">{profile.avatar}</span>
                      <div className="text-left hidden sm:block">
                        <div className="font-medium text-gray-800">{profile.username}</div>
                        <div className={`text-xs ${userState.statusColor}`}>
                          <span className="mr-1">{userState.statusIcon}</span>
                          {userState.displayName}
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Sign In Button */}
                  <button
                    onClick={handleSignInClick}
                    disabled={!userState.canSignIn}
                    className={`btn-primary text-sm px-4 py-2 ${
                      !userState.canSignIn ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    title={!userState.canSignIn ? 'Sign in requires internet connection' : 'Sign in to your account'}
                  >
                    Sign In
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
};

export default Header; 